import { useChat } from "@ai-sdk/react";
import { RiArrowLeftLine, RiBardFill, RiFileCopyLine } from "@remixicon/react";
import { useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { marked } from "marked";
import { nanoid } from "nanoid";
import { useEffect, useRef } from "react";
import toast, { Toaster } from "react-hot-toast";
import tipToast from "~/components/edit-tip-toaster";
import EditorComponent from "~/components/editor/editor";
import InputEvaluator from "~/components/input-evaluator";
import Logo from "~/components/logo";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { useFlags } from "~/hooks/use-flags";
import { useIsSticky } from "~/hooks/use-sticky";
import { cn } from "~/lib/utils";
import * as Sentry from "@sentry/react";

export function meta() {
  return [
    { title: "Vacature hulp - JEX" },
    { name: "description", content: "Maak de perfecte vacature met JEX AI!" },
  ];
}

const extensions = [StarterKit];

export default function Home() {
  const flags = useFlags();
  const url = new URL(`${flags.get("ELOQUENT_URL")}/api/chat`);
  url.searchParams.append(
    "domain-identifier",
    flags.get("ELOQUENT_VACANCY_IDENTIFIER", "")
  );

  const chatIdentifier = nanoid();

  const { messages, append, isLoading, input, setInput } = useChat({
    api: url.toString(),
    body: {
      chatIdentifier,
    },
  });

  const toastId = useRef<string>(null);

  useEffect(() => {
    if (isLoading && !toastId.current) {
      toastId.current = toast.loading("Vacature aan het genereren...");
    } else if (!isLoading && toastId.current) {
      toast.success("Vacature gegenereerd!", { id: toastId.current });
      toastId.current = null;
    }
  }, [isLoading]);

  const lastMessage = messages[messages.length - 1];

  const isAssistantMessage = lastMessage?.role === "assistant";

  useEffect(() => {
    if (isAssistantMessage && !isLoading) {
      tipToast("Tip: je kan de vacaturetekst bewerken!");
    }
  }, [isAssistantMessage, isLoading]);

  useEffect(() => {
    if (isLoading) {
      const vacancyElement = document.getElementById("vacancy");
      if (vacancyElement) {
        vacancyElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }
  }, [isLoading]);

  const content = marked(lastMessage?.content || "") as string;

  const editor = useEditor(
    {
      extensions,
      content,
      onTransaction: async ({ editor }) => {
        setInput(editor.getHTML());
      },
    },
    [content]
  );

  const sticky = useIsSticky("#editor-bar");

  const width = "mx-auto w-full max-w-3xl sm:px-6 lg:px-8 py-6";

  return (
    <>
      <Toaster position="bottom-center" />
      <nav className="border-b-1 border-gray-300 p-6">
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Logo />
        </div>
      </nav>
      <main className="transition-all flex flex-col gap-12 pt-6 pb-24 duration-300">
        <div className={cn("flex flex-col gap-6", width)} id="description">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">
              Slim, snel en spot-on: Onze AI-agent schrijft jouw vacature
            </h1>
            <p className="mb-4">
              Onze vacature AI-agent is getraind door onze beste recruiters – en
              nu kun jij die expertise ook gebruiken! Geef een paar details,
              zoals de functietitel, belangrijkste taken en de skills die je
              zoekt. Hoe meer info je geeft, hoe beter onze AI-agent een
              strakke, pakkende vacaturetekst voor je opstelt. Geen gedoe, geen
              standaardteksten – gewoon een perfecte vacature in no time.
            </p>
          </div>
          <InputEvaluator disabled={isLoading} appendVacancyMessage={append} />
        </div>
        {lastMessage && (
          <>
            <Separator />
            <div
              className={cn("min-h-screen flex flex-col gap-6 py-6", width)}
              id="vacancy"
            >
              <div
                id="editor-bar"
                className={cn(
                  "z-50 sticky bg-background items-center right-10 transition-all rounded-2xl top-2 justify-end w-full flex gap-2 border border-transparent",
                  sticky && "border-border shadow p-2 pl-5"
                )}
              >
                <p className="w-full text-xl font-semibold">Vacature</p>

                <Button
                  variant={"outline"}
                  disabled={isLoading}
                  onClick={() => {
                    scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <RiArrowLeftLine />
                  Terug
                </Button>
                <Button
                  type="button"
                  variant={"outline"}
                  size={"withIcon"}
                  onClick={() => {
                    console.log(input);

                    console.log("Opnieuw genereren");

                    append({
                      role: "user",
                      content:
                        "Please improve the following vacancy by possibly completing the user comments or adding more information: \n\n" +
                        input,
                    });
                  }}
                  disabled={isLoading}
                >
                  <RiBardFill />
                  Opnieuw genereren
                </Button>
                <Button
                  disabled={isLoading}
                  onClick={async () => {
                    try {
                      const textItem = {
                        "text/plain": input.replace(/<[^>]*>/g, "\n"),
                        "text/html": input,
                      };

                      const clipboardItem = new ClipboardItem(textItem);

                      await navigator.clipboard.write([clipboardItem]);

                      toast.success("Gekopieerd naar klembord!");
                    } catch (err) {
                      console.error("Error copying to clipboard: ", err);

                      Sentry.captureException(err);

                      toast.error("Error copying to clipboard");
                    }
                  }}
                >
                  <RiFileCopyLine />
                  Kopiëren
                </Button>
              </div>
              <Separator id="vacancy" />
              <EditorComponent
                className={cn(!isAssistantMessage && "hidden")}
                isLoading={isLoading}
                editor={editor}
              />
            </div>
          </>
        )}
      </main>
    </>
  );
}
